import { useContext, useMemo } from "react"
import { ScenarioStatesProviderContext } from "../providers/StatesProvider";
import { createGroundPacket } from "../utils/viewer-packets";
import { ScenarioFormDataType } from "../utils/scenario-types";
import useNavigation from "./useNavigation";
import { getViewerPacketDetails } from "../utils/helperFunction";
import { requestRender, resizeViewerPosition } from "../../../../utils/cesiumHelperFunctions";
import * as Cesium from "cesium";

const VIEWER_MORPH_DURATION_SECONDS = 0.2;

const waitForMorphComplete = (viewer: Cesium.Viewer) => new Promise<void>((resolve) => {
    if (viewer.isDestroyed()) {
        resolve()
        return
    }

    let removeMorphListener: (() => void) | undefined;
    const timeout = window.setTimeout(() => {
        removeMorphListener?.();
        resolve()
    }, (VIEWER_MORPH_DURATION_SECONDS * 1000) + 250)

    removeMorphListener = viewer.scene.morphComplete.addEventListener(() => {
        window.clearTimeout(timeout)
        removeMorphListener?.();
        resolve()
    })
})

const useViewer = () => {
    const { scenarioId } = useNavigation()
    const context = useContext(ScenarioStatesProviderContext);
    if (context === undefined) {
        throw new Error('useViewer must be used within a Scenario State Provider');
    }
    const { viewer, setViewer, scenarioBaseData } = context;
    const { allGeoFeatures, allTaskPlans, allStations, allFaults } = useMemo(() => scenarioBaseData, [scenarioBaseData])

    const updateGroundPackets = (formData: ScenarioFormDataType) => {
        const { allGroundStations, selectedStations, selectedTaskplans, selectedFaultPlans } = getViewerPacketDetails(formData, allTaskPlans, allGeoFeatures, allFaults, allStations)
        createGroundPacket(viewer, allGroundStations, selectedStations, selectedTaskplans, selectedFaultPlans)
    }

    const updateViewerMode = async (mode: '2D' | '3D') => {
        if (viewer && scenarioId && !viewer.isDestroyed()) {
            try {
                const previous_map_details = JSON.parse(sessionStorage.getItem('map_details') || '{}');
                const viewerDetails = previous_map_details?.[scenarioId];
                const manual3DMode = viewerDetails?.manual3DMode;
                if (!manual3DMode) {
                    const updatedMapDetails = {
                        ...previous_map_details,
                        [scenarioId]: {
                            ...viewerDetails,
                            View: mode,
                        },
                    };
                    sessionStorage.setItem("map_details", JSON.stringify(updatedMapDetails));
                    if (mode === "2D") {
                        viewer.scene.morphTo2D(VIEWER_MORPH_DURATION_SECONDS);
                        await waitForMorphComplete(viewer)
                        if (!viewer.isDestroyed()) {
                            resizeViewerPosition(viewer);
                        }
                    } else {
                        viewer.scene.morphTo3D(VIEWER_MORPH_DURATION_SECONDS);
                        await waitForMorphComplete(viewer)
                        requestRender(viewer);
                    }
                }
            } catch (e) {
                console.error('Error updating viewer mode:', e);
            }
        }
    };

    return { viewer, setViewer, updateGroundPackets, updateViewerMode };
}

export default useViewer
